import { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
// i18next
import { useTranslation } from 'react-i18next';
// Types
import MediaFormats from 'types/MediaFormats';
// MUI
import { ButtonGroup } from '@material-ui/core';
// Components
import ImageUpload from 'components/ImageUpload';
import Accordion from 'components/Accordion';
import MultiSelect from 'components/Autocomplete';
import { ColorPicker } from 'components/ColorPicker';
import StyledMuiButton from 'components/StyledMuiButton';
// Utilities
import { isRequired } from 'utilities/validation';
import { sponsorLogoPagesOptions } from 'utilities/utilities';

const SectionImages:FC = () => {
  const { t } = useTranslation();

  const { control, watch, formState: { errors } } = useFormContext();
  const quizType = watch('type');
  const logoImageBackgroundColor = watch('logoImageBackgroundColor');
  const sponsorImageBackgroundColor = watch('sponsorImageBackgroundColor');
  const logoFormat = watch('logoFormat');
  const sponsorImageFormat = watch('sponsorImageFormat');

  return (
    <div react-scroll="images">
      <Accordion label={t('labels.images')} icon="images">
        <div className="w-full">
          <span className="block text-gray-400 text-sm font-light leading-5 mb-4">{t('pages.quizForm.imagesHint')}</span>
          <Controller
            control={control} name="logoFormat"
            render={({ field: { onChange, value } }) => (
              <ButtonGroup size="small" style={{ marginBottom: '16px' }}>
                <StyledMuiButton
                  variant={value === MediaFormats.Logo ? 'contained' : 'outlined'}
                  onClick={() => onChange(MediaFormats.Logo)}
                >{t('labels.squareLogo')}</StyledMuiButton>
                <StyledMuiButton
                  variant={value === MediaFormats.RectLogo ? 'contained' : 'outlined'}
                  onClick={() => onChange(MediaFormats.RectLogo)}
                >{t('labels.rectangularLogo')}</StyledMuiButton>
              </ButtonGroup>
            )}
          />
          <Controller
            control={control} name="logoImage" rules={{ required: isRequired }}
            render={({ field }) => (
              <ImageUpload
                {...field}
                key={`${logoFormat}-${logoImageBackgroundColor}`}
                className="w-1/3"
                format={logoFormat}
                required={true}
                label="labels.logoImage"
                error={Boolean(errors.logoImage)}
                errorText={errors.logoImage ? errors.logoImage.message : ''}
                backgroundColor={logoImageBackgroundColor}
              />
            )}
          />
          <div className="mb-4">
            <Controller
              control={control} name="logoImageBackgroundColor"
              render={({ field: { value, onChange } }) => (
                <ColorPicker
                  label={t('labels.logoImageBackgroundColor')}
                  value={value || 'rgba(255, 255, 255, 0)'}
                  onChange={onChange}
                />
              )}
            />
          </div>
          <Controller
            control={control} name="sponsorImageFormat"
            render={({ field: { onChange, value } }) => (
              <ButtonGroup size="small" style={{ marginBottom: '16px' }}>
                <StyledMuiButton
                  variant={value === MediaFormats.Logo ? 'contained' : 'outlined'}
                  onClick={() => onChange(MediaFormats.Logo)}
                >{t('labels.squareLogo')}</StyledMuiButton>
                <StyledMuiButton
                  variant={value === MediaFormats.RectLogo ? 'contained' : 'outlined'}
                  onClick={() => onChange(MediaFormats.RectLogo)}
                >{t('labels.rectangularLogo')}</StyledMuiButton>
              </ButtonGroup>
            )}
          />
          <Controller
            control={control} name="sponsorImage"
            render={({ field }) => (
              <ImageUpload
                {...field}
                key={`${sponsorImageFormat}-${sponsorImageBackgroundColor}`}
                className="w-1/3"
                format={sponsorImageFormat}
                label="pages.quizForm.sponsorImageLabel"
                helperText="pages.quizForm.sponsorImageHint"
                backgroundColor={sponsorImageBackgroundColor}
              />
            )}
          />
          <div className="mb-4">
            <Controller
              control={control} name="sponsorImageBackgroundColor"
              render={({ field: { value, onChange } }) => (
                <ColorPicker
                  label={t('pages.quizForm.sponsorImageBackgroundColorLabel')}
                  value={value || 'rgba(255, 255, 255, 0)'}
                  onChange={onChange}
                />
              )}
            />
          </div>
          <Controller
            control={control} name="sponsorImagePages"
            render={({ field }) => (
              <MultiSelect
                {...field}
                label={t('pages.quizForm.showSponsorLogoOnLabel')}
                options={sponsorLogoPagesOptions(quizType)}
                helperText={t('pages.quizForm.showSponsorLogoOnHint')}
              />
            )}
          />
          <Controller
            control={control} name="wallpaperImage"
            render={({ field }) => (
              <ImageUpload
                {...field}
                format={MediaFormats.GameWallpaper}
                label="labels.wallpaperImage"
                helperText="pages.quizForm.wallpaperImageHint"
              />
            )}
          />
          <div className="flex flex-wrap">
            <div className="">
              <Controller
                control={control} name="backgroundImage"
                render={({ field }) => (
                  <ImageUpload
                    {...field}
                    format={MediaFormats.Background}
                    label="labels.backgroundImage"
                    className="sm:w-1/2"
                  />
                )}
              />
              <span className="block text-gray-400 text-sm font-light leading-5 mb-4">
                {t('pages.quizForm.backgroundImageHint')}
              </span>
            </div>
          </div>
        </div>
      </Accordion>
    </div>
  )
}

export default SectionImages;
